import formatDate from './format-date';

const iconSizes = ['small', 'medium', 'large'];

/**
 * Get cover art url from playlist item metadata
 *
 * @throws {Error} Size must be one of 'small', 'medium' or 'large'
 * @param {Object[]} icons Playlist item icons
 * @param {String}   size  The desired size, may only be on of small, medium or large
 * @return {String}
 */
export function getIconUrl (icons, size = 'small') {
  if (iconSizes.indexOf(size) === -1) {
    throw new Error(`Invalid cover art size, expected one of 'small', 'medium' or 'large', but got ${size}'`);
  }

  const mappedIcons = (icons || [])
    .reduce(function (icons, icon) {
      icons[icon.size] = icon.url;
      return icons;
    }, {});

  if (mappedIcons[size]) {
    return mappedIcons[size];
  }

  for (let i = 0, len = iconSizes.length; i < len; i++) {
    if (mappedIcons[iconSizes[i]]) {
      return mappedIcons[iconSizes[i]];
    }
  }
}

/**
 * Get the playlist item website url from metadata
 *
 * @param  {iono.Player} player
 * @return {String|null}
 */
export function metadataUrl (player) {
  const playlistItem = player.getPlaylistItem();

  if (!playlistItem || !playlistItem.metadata.url) {
    return null;
  }

  return playlistItem.metadata.url;
}

/**
 * Get download url for current playlist item
 *
 * @param  {iono.Player} player
 * @return {String|null}
 */
export function metadataDownloadUrl (player) {
  const currentSource = player.getSource();

  if (currentSource) {
    return (currentSource.download || currentSource.url);
  }

  return '';
}

/**
 * Get cover art url for the current playing item
 *
 * @param {iono.Player} player
 * @param {String}      size
 * @return {String}
 */
export function metadataIcon (player, size = 'small') {
  const nowPlayingMetadata = player.getNowPlayingItem();
  const playlistItem = player.getPlaylistItem() || {};

  let metadata = playlistItem && playlistItem.metadata;
  if (nowPlayingMetadata && nowPlayingMetadata.icons && nowPlayingMetadata.icons.length > 0) {
    metadata = nowPlayingMetadata;
  }

  if (!metadata) {
    return '';
  }

  return getIconUrl(metadata.icons || [], size);
}

/**
 * Get the metadata title of the current playing item
 *
 * @param {iono.Player} player
 * @return {String}
 */
export function metadataTitle (player) {
  const playlistItem = player.getPlaylistItem();
  const metadata = playlistItem.metadata || {};

  if (player.isLive()) {
    const nowPlayingMetadata = player.getNowPlayingItem() || {};
    if (nowPlayingMetadata.creator) {
      return `${nowPlayingMetadata.creator} - ${nowPlayingMetadata.title}`;
    } else if (nowPlayingMetadata.title) {
      return nowPlayingMetadata.title;
    } else {
      return player.locale.player.live_stream;
    }
  }

  return metadata.title;
}

/**
 * Get the metadata grouping of the current playing item
 *
 * @param {iono.Player} player
 * @return {String}
 */
export function metadataGrouping (player, options = {
  includeDate: false,
  dateFormat: '',
  locale: {}
}) {
  const metadata = player.getPlaylistItem().metadata;

  if (!metadata) return '';

  if (player.isLive()) {
    return (metadata.creator || metadata.title);
  }

  if (options.includeDate) {
    let pubDate = metadataPublishDate(player);

    if (pubDate) {
      pubDate = formatDate(pubDate, options.dateFormat, options.locale).toUpperCase();

      return `${pubDate}${metadata.grouping ? ` / ${metadata.grouping}` : ''}`;
    }
  }

  return metadata.grouping;
}

/**
 * Get the metadata publish date of the current playing item
 *
 * @param {iono.Player} player
 * @return {Date}
 */
export function metadataPublishDate (player) {
  const metadata = player.getPlaylistItem().metadata;

  if (!metadata || player.isLive()) return null;

  return metadata.publishDate;
}
